import { FunctionComponent, useState } from 'react';
import { ExpandableSelector } from 'components/UI/V3/ExpandableSelector';
import { ExpandableSelectorList } from 'components/UI/V3/ExpandableSelector/List/ExpandableSelectorList';
import { ExpandableSelectorItem } from 'components/UI/V3/ExpandableSelector/List/ExpandableSelectorItem';
import styles from './Schedule.module.scss';
import { Moment } from 'moment-timezone';

interface MedicalScheduleProps {
    id: number;
    value: string;
    from?: Moment | undefined;
    to?: Moment | undefined;
    onChange: (time: string) => void;
    placeholder?: string;
    label?: string;
    usedTimes?: string[];
}

const Schedule: FunctionComponent<MedicalScheduleProps> = ({
    id,
    value,
    onChange,
    label,
    from,
    to,
    usedTimes = []
}) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const generateTimeIntervals = (): string[] => {
        const intervals: string[] = [];

        if (from && to) {
            const startDate = from.clone();
            const endDate = to.clone();

            const startMinutes = startDate.hour() * 60 + startDate.minute();
            const endMinutes = endDate.hour() * 60 + endDate.minute();

            if (startMinutes > endMinutes) {
                const endOfDay = startDate.clone().endOf('day');
                const startDayMinutes = startDate.hour() * 60 + startDate.minute();
                const endDayMinutes = 24 * 60;
                const startDayIntervals = Math.floor((endDayMinutes - startDayMinutes) / 15) + 1;

                for (let i = 0; i < startDayIntervals; i++) {
                    const currentTime = startDate.clone().add(i * 15, 'minutes');

                    if (currentTime.isAfter(endOfDay)) {
                        break;
                    }

                    const timeString = currentTime.format('h:mmA').toLowerCase();
                    intervals.push(timeString);
                }

                const startOfNextDay = endDate.clone().startOf('day');
                const nextDayStartMinutes = 0;
                const nextDayEndMinutes = endDate.hour() * 60 + endDate.minute();
                const nextDayIntervals = Math.floor((nextDayEndMinutes - nextDayStartMinutes) / 15) + 1;

                for (let i = 0; i < nextDayIntervals; i++) {
                    const currentTime = startOfNextDay.clone().add(i * 15, 'minutes');

                    if (currentTime.isAfter(endDate)) {
                        break;
                    }

                    const timeString = currentTime.format('h:mmA').toLowerCase();
                    intervals.push(timeString);
                }
            } else {
                const totalMinutes = endDate.diff(startDate, 'minutes');
                const numberOfIntervals = Math.floor(totalMinutes / 15) + 1;

                for (let i = 0; i < numberOfIntervals; i++) {
                    const currentTime = startDate.clone().add(i * 15, 'minutes');

                    if (currentTime.isAfter(endDate)) {
                        break;
                    }

                    const timeString = currentTime.format('h:mmA').toLowerCase();

                    intervals.push(timeString);
                }
            }
        } else {
            for (let hour = 0; hour < 24; hour++) {
                for (let minute = 0; minute < 60; minute += 15) {
                    const date = new Date();
                    date.setHours(hour, minute, 0, 0);

                    const timeString = date
                        .toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        })
                        .replace(' ', '')
                        .toLowerCase();

                    intervals.push(timeString);
                }
            }
        }

        return intervals;
    };

    const timeIntervals = generateTimeIntervals();

    const normalizeTime = (time: string): string => {
        return time.replace(/\s/g, '').toUpperCase();
    };

    const normalizedUsedTimes = usedTimes.map(normalizeTime);
    const normalizedCurrentValue = normalizeTime(value);

    const availableTimeIntervals = timeIntervals.filter(time => {
        const normalizedTime = normalizeTime(time);
        return normalizedTime === normalizedCurrentValue || !normalizedUsedTimes.includes(normalizedTime);
    });

    const handleTimeChange = (time: string) => {
        onChange(time.toUpperCase());
        setExpanded(false);
    };

    return (
        <div className={styles.timeSelectorContainer}>
            {label && <label className={styles.label}>{label}</label>}

            <ExpandableSelector
                id={'Dose' + id}
                expanded={expanded}
                onExpand={() => setExpanded(!expanded)}
                onClickAway={() => setExpanded(false)}
                className={styles.timeSelector}
                childrenClassName={styles.list}
            >
                <ExpandableSelectorList>
                    {availableTimeIntervals.map((time, timeIndex) => (
                        <ExpandableSelectorItem
                            key={timeIndex}
                            selected={value === time}
                            onClick={() => handleTimeChange(time)}
                        >
                            {time.replace(' ', '').toLowerCase()}
                        </ExpandableSelectorItem>
                    ))}
                </ExpandableSelectorList>
            </ExpandableSelector>
        </div>
    );
};

export default Schedule;
